import { useFormState } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { Box, styled } from '@mui/material';

import { AlertError } from 'Common/AlertError';
import { AlertSuccess } from 'Common/AlertSuccess';
import { FormButtons, PasswordAndConfirmPasswordFields, PasswordField } from 'Common/Form';
import { ChangePasswordSchema } from 'Common/Form/ValidationSchemas';
import { Spacing } from 'Themes/spacing';
import { useAppForm } from 'Utils/customHooks';

import type { IChangePasswordRequest } from '@/libs/models';

const formFieldNames = {
  oldPassword: 'oldPassword',
  password: 'password',
  confirmPassword: 'confirmPassword'
} as const;

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.medium};
`;

type Props = {
  onSubmit: (formData: IChangePasswordRequest) => void;
  onCancel: () => void;
  error?: string | null;
  isSubmitSuccess?: boolean;
};

export const ChangePasswordForm = ({
  onSubmit,
  onCancel,
  error,
  isSubmitSuccess
}: Readonly<Props>) => {
  const { t } = useTranslation();
  const formContext = useAppForm<IChangePasswordRequest>(ChangePasswordSchema());
  const { isValid } = useFormState({ control: formContext.control });

  return (
    <FormContainer onSuccess={onSubmit} formContext={formContext}>
      <Container>
        {error && <AlertError text={t(error)} />}

        {isSubmitSuccess && <AlertSuccess text={t('auth.passwordHasBeenChanged')} />}

        <PasswordField name={formFieldNames.oldPassword} label={t('auth.oldPassword')} />

        <PasswordAndConfirmPasswordFields
          formContext={formContext}
          passwordLabel={t('auth.newPassword')}
        />

        <FormButtons
          onSubmit={() => {}}
          onCancel={onCancel}
          submitText={t('Save')}
          cancelText={t('Cancel')}
          isDisabled={!isValid}
        />
      </Container>
    </FormContainer>
  );
};
